import React, { FC, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Header1, Wrapper } from './myAccountStyles';

interface DigitalDownloadRow {
  ordered_at: string;
  student_name: string | null;
  url: string;
  expires_at: string | null;
  expired: boolean;
}

const Table = styled.table`
  margin-bottom: 0;
`;

const Row = styled.tr<{ $expired: boolean }>`
  color: ${({ $expired }) => ($expired ? '#999' : 'inherit')};
`;

const DownloadLink = styled.a`
  color: #479de6;
  font-weight: 600;
`;

// UTC, because Order History's dates are formatted server-side in the app's
// (UTC) zone; formatting in the browser's zone could show the same purchase
// on two different days.
const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

// Card listing the parent's digital download links. Renders nothing until it
// knows there is at least one download, so parents without any never see it.
const MyDigitalDownloads: FC = () => {
  const [downloads, setDownloads] = useState<DigitalDownloadRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    axios
      .get('/api/v2/parent/digital_downloads')
      .then((response) => {
        if (!cancelled) setDownloads(response.data?.digital_downloads ?? []);
      })
      .catch((err) => {
        console.error('Failed to fetch digital downloads:', err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (downloads.length === 0) return null;

  return (
    <Wrapper>
      <Header1>My Digital Downloads</Header1>
      <hr />
      <div className="table-responsive">
        <Table className="table">
          <thead>
            <tr>
              <th>Ordered</th>
              <th>Student</th>
              <th>Download</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {downloads.map((download) => (
              // A legacy link can be shared by two orders, so the url alone
              // is not unique.
              <Row
                key={`${download.ordered_at}-${download.url}`}
                $expired={download.expired}
              >
                <td>{formatDate(download.ordered_at)}</td>
                <td translate="no">{download.student_name}</td>
                <td>
                  {download.expired ? (
                    'Expired'
                  ) : (
                    <DownloadLink
                      href={download.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </DownloadLink>
                  )}
                </td>
                <td>
                  {download.expires_at
                    ? formatDate(download.expires_at)
                    : 'Never'}
                </td>
              </Row>
            ))}
          </tbody>
        </Table>
      </div>
    </Wrapper>
  );
};

export default MyDigitalDownloads;
